import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { MoreVertical } from "lucide-react";

export interface RowAction {
  label: string;
  icon?: React.ComponentType<{ className?: string }>;
  onClick: () => void;
  destructive?: boolean;
  disabled?: boolean;
}

interface RowActionsMenuProps {
  actions: RowAction[];
  disabled?: boolean;
}

/** Kebab menu for table row actions — the one row-action convention list
 * pages should converge on, since icon+text rows wrap awkwardly once a row
 * carries more than 2-3 actions, and icon-only rows lose discoverability for
 * less-common actions. Positioning mirrors ReassignPopover/StatusChangePopover. */
export default function RowActionsMenu({ actions, disabled }: RowActionsMenuProps) {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; right: number } | null>(null);

  useEffect(() => {
    if (!open) return;
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open]);

  function toggle(e: React.MouseEvent) {
    e.stopPropagation();
    if (!open && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setPos({ top: rect.bottom + 4, right: Math.max(8, window.innerWidth - rect.right) });
    }
    setOpen((v) => !v);
  }

  function runAction(action: RowAction) {
    setOpen(false);
    if (!action.disabled) action.onClick();
  }

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={toggle}
        disabled={disabled}
        title="More actions"
        className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-surface-muted rounded-lg transition-colors disabled:opacity-50"
      >
        <MoreVertical className="w-4 h-4" />
      </button>

      {open && pos &&
        createPortal(
          <>
            <div className="fixed inset-0 z-[80]" onClick={(e) => { e.stopPropagation(); setOpen(false); }} />
            <div
              className="fixed z-[81] w-48 bg-surface border border-border rounded-xl shadow-2xl shadow-black/30 overflow-hidden py-1"
              style={{ top: pos.top, right: pos.right }}
              onClick={(e) => e.stopPropagation()}
            >
              {actions.map((action, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => runAction(action)}
                  disabled={action.disabled}
                  className={`w-full flex items-center gap-2.5 px-3.5 py-2 text-sm font-inter transition-colors disabled:opacity-50 text-left ${
                    action.destructive
                      ? "text-destructive hover:bg-destructive/10"
                      : "text-foreground hover:bg-surface-muted"
                  }`}
                >
                  {action.icon && <action.icon className="w-4 h-4 shrink-0" />}
                  {action.label}
                </button>
              ))}
            </div>
          </>,
          document.body,
        )}
    </>
  );
}
